import { Controller } from "@hotwired/stimulus"

/**
 * StickyNav Controller
 * - Sticky navigation que aparece al hacer scroll
 * - Scroll spy: detecta sección visible y marca link activo
 * - Auto-scroll horizontal: centra el link activo en el track
 * - Click/tap: navega a la sección con offset correcto
 */
export default class extends Controller {
  static targets = ["link", "track"]
  static values = {
    headerOffset: { type: Number, default: 60 },
    trackScrollLeft: Number
  }

  connect() {
    this.sections = document.querySelectorAll('[id^="section-"]')
    if (this.sections.length === 0) return

    this.isScrollingToSection = false // Flag para evitar rebote
    this.sentinel = this.element.previousElementSibling

    // Un snapshot de Turbo conserva la seccion activa. Recuperarla evita que,
    // al volver con scroll restaurado, la primera categoria se active antes de
    // que se restaure la posicion vertical de la pagina.
    this.currentSection = this.linkTargets.find(link =>
      link.classList.contains("active")
    )?.dataset.section

    this.setupScrollListener()
    this.updateInitialState()
  }

  disconnect() {
    window.removeEventListener("scroll", this.scrollHandler)
    window.removeEventListener("resize", this.resizeHandler)
    cancelAnimationFrame(this.layoutFrame)
    clearTimeout(this.scrollTimeout)
  }

  // ========================================
  // Scroll Spy
  // ========================================

  updateActiveSection() {
    if (this.isScrollingToSection) return

    // La seccion actual es la ultima cuyo titulo alcanzo la misma linea que
    // usa el click. Ver un titulo mas abajo no debe adelantar el chip.
    // 1px absorbe el redondeo del scroll al terminar una navegacion suave.
    const threshold = this.sectionOffset + 1
    let activeSection = this.sections[0]
    for (const section of this.sections) {
      if (section.getBoundingClientRect().top > threshold) break
      activeSection = section
    }
    if (activeSection) this.activateLink(activeSection.id)
  }

  // ========================================
  // Sticky Behavior
  // ========================================

  setupScrollListener() {
    this.scrollHandler = this.throttle(() => {
      this.updateStickyState()
      this.updateActiveSection()
    }, 16)
    this.resizeHandler = this.throttle(() => this.scheduleLayoutRefresh(), 250)

    window.addEventListener("scroll", this.scrollHandler, { passive: true })
    window.addEventListener("resize", this.resizeHandler, { passive: true })
  }

  get headerOffset() {
    const top = Number.parseFloat(getComputedStyle(this.element).top)
    return Number.isFinite(top) ? top : this.headerOffsetValue
  }

  get sectionOffset() {
    const navHeight = this.element.offsetHeight || 50
    const extraPadding = window.innerWidth < 768 ? 16 : 24
    return this.headerOffset + navHeight + extraPadding
  }

  scheduleLayoutRefresh() {
    cancelAnimationFrame(this.layoutFrame)

    // Turbo puede restaurar el scroll despues de conectar Stimulus. Esperar dos
    // frames permite reconciliar la posicion sin ocultar el sticky conservado
    // en el snapshot durante esos frames.
    this.layoutFrame = requestAnimationFrame(() => {
      this.layoutFrame = requestAnimationFrame(() => this.refreshLayout())
    })
  }

  refreshLayout() {
    this.updateNavPosition()
    this.updateStickyState()
    this.updateActiveSection()
  }

  updateNavPosition() {
    // El sentinel permanece en el flujo aunque el nav use position: fixed.
    // Asi se puede medir el umbral real sin quitar `sticky` temporalmente.
    const anchor = this.sentinel?.matches("[data-sticky-nav-sentinel]")
      ? this.sentinel
      : this.element

    this.navInitialTop = anchor.getBoundingClientRect().top + window.scrollY
  }

  updateStickyState() {
    if (!Number.isFinite(this.navInitialTop)) return

    const scrollY = window.scrollY
    const threshold = this.navInitialTop - this.headerOffset

    if (scrollY >= threshold) {
      this.element.classList.add("sticky")
    } else {
      this.element.classList.remove("sticky")
    }
  }

  updateInitialState() {
    // Medimos inmediatamente, pero conservamos la clase que venga en el
    // snapshot. La reconciliacion diferida ocurrira una vez restaurado el scroll.
    this.updateNavPosition()
    this.scheduleLayoutRefresh()

    // Turbo clona los atributos, pero no el scroll de contenedores con overflow.
    // Restaurar incluso 0 y posiciones manuales, sin re-centrar el chip activo.
    if (this.hasTrackTarget && this.hasTrackScrollLeftValue) {
      this.trackTarget.scrollTo({ left: this.trackScrollLeftValue, behavior: "instant" })
    } else if (!this.currentSection && this.sections.length > 0) {
      // Solo activar la primera seccion en una visita nueva.
      this.activateLink(this.sections[0].id)
    } else if (this.currentSection) {
      // Respaldo para snapshots anteriores. `auto` heredaria el CSS smooth.
      const activeLink = this.linkTargets.find(
        link => link.dataset.section === this.currentSection
      )
      if (activeLink) this.centerLinkInTrack(activeLink, { behavior: "instant" })
    }
  }

  saveScrollPosition() {
    if (this.hasTrackTarget) this.trackScrollLeftValue = this.trackTarget.scrollLeft
  }

  // ========================================
  // Link Activation & Auto-Scroll
  // ========================================

  activateLink(sectionId) {
    // Evitar re-activar el mismo link
    if (this.currentSection === sectionId) return
    this.currentSection = sectionId

    // Quitar active de todos los links
    this.linkTargets.forEach(link => link.classList.remove("active"))

    // Encontrar y activar el link correspondiente
    const activeLink = this.linkTargets.find(
      link => link.dataset.section === sectionId
    )

    if (activeLink) {
      activeLink.classList.add("active")
      this.centerLinkInTrack(activeLink)
    }
  }

  centerLinkInTrack(link, { behavior = "smooth" } = {}) {
    if (!this.hasTrackTarget) return

    const track = this.trackTarget

    // Calcular posición para centrar el link
    const linkCenter = link.offsetLeft + (link.offsetWidth / 2)
    const trackCenter = track.clientWidth / 2
    const scrollTo = linkCenter - trackCenter

    track.scrollTo({
      left: Math.max(0, scrollTo),
      behavior
    })
  }

  // ========================================
  // Click Handler (Navigate to Section)
  // ========================================

  scrollToSection(event) {
    event.preventDefault()

    const sectionId = event.currentTarget.dataset.section
    const section = document.getElementById(sectionId)
    if (!section) return

    // Evitar cambios intermedios de categoria durante el scroll programatico.
    this.isScrollingToSection = true

    // Dibuja los contenedores diferidos (.store-lazy) antes de medir; store-lazy los vuelve a diferir al cachear.
    // Ver storefront_catalog.md → Restore de la tienda
    document.querySelectorAll(".store-lazy").forEach((element) => { element.style.contentVisibility = "visible" })

    // Scroll suave a la sección
    const targetY = section.getBoundingClientRect().top + window.scrollY - this.sectionOffset

    window.scrollTo({
      top: targetY,
      behavior: "smooth"
    })

    // Feedback inmediato
    this.activateLink(sectionId)

    // Haptic feedback en móvil
    if ("vibrate" in navigator) {
      navigator.vibrate(10)
    }

    // Desactivar flag después del scroll (800ms es suficiente para smooth scroll)
    clearTimeout(this.scrollTimeout)
    this.scrollTimeout = setTimeout(() => {
      this.isScrollingToSection = false
    }, 800)
  }

  // ========================================
  // Utilities
  // ========================================

  throttle(func, limit) {
    let inThrottle
    return (...args) => {
      if (!inThrottle) {
        func.apply(this, args)
        inThrottle = true
        setTimeout(() => inThrottle = false, limit)
      }
    }
  }
}
